import React from 'react';
import moment from 'moment';
import {getLandlordLevelLabel, LANDLORD_LEVEL_LABELS} from "./TopLandlordIcon";

/*
 * Mostra o retrato que o job diario do backend guarda em landlordLevelMetrics: as metricas dos
 * criterios do nivel, os limites do escalao do senhorio e os criterios que falha para cada nivel.
 * O snapshot so existe depois do job correr pela primeira vez para este senhorio.
 */

const CRITERION_LABELS = {
    confirmed_reservations: "Confirmed bookings",
    cancellation_rate: "Cancellation rate",
    answer_ratio: "Request answer rate",
    availability_update: "Availability confirmation",
    confirmed_reservation_recency: "Booking recency"
};

const formatDays = (value) => (value === null || value === undefined ? "never" : `${value} days`);

const Metric = ({label, value, goal, failed}) => (
    <div className="col-md-4 mb-3">
        <p className="label text-muted mb-0">{label}</p>
        <p className={"number mb-0 " + (failed ? "text-danger" : "text-dark")}>{value}</p>
        {goal && <small className="text-muted">Goal: {goal}</small>}
    </div>
);

const FailedList = ({title, failed}) => (
    <div className="col-md-6">
        <p className="label text-muted mb-1">{title}</p>
        {failed && failed.length
            ? <ul className="mb-0 pl-3">{failed.map(k => <li key={k}>{CRITERION_LABELS[k] || k}</li>)}</ul>
            : <span className="badge badge-success">All criteria met</span>}
    </div>
);

const LandlordLevelPanel = ({landlord}) => {
    const metrics = landlord?.landlordLevelMetrics;

    if (!metrics) {
        return (
            <div className="card">
                <div className="card-body">
                    <h4 className="card-title">Account Level</h4>
                    <p className="text-muted mb-0">
                        No calculation recorded yet for this landlord. The level is computed by the daily job.
                    </p>
                </div>
            </div>
        );
    }

    const thresholds = metrics.thresholds || {};
    const minConfirmed = thresholds.minConfirmedReservations || {};
    const failedTop = (metrics.failed && metrics.failed.topLandlord) || [];
    const failedRising = (metrics.failed && metrics.failed.risingStar) || [];
    const failing = (k) => failedTop.includes(k);

    return (
        <div className="card">
            <div className="card-body">
                <h4 className="card-title d-flex align-items-center">
                    Account Level
                    <span className="badge badge-secondary text-dark ml-3">{getLandlordLevelLabel(landlord)}</span>
                    {landlord.landlordLevelOverride &&
                        <span className="badge badge-info text-dark ml-2">
                            Pinned: {LANDLORD_LEVEL_LABELS[landlord.landlordLevelOverride] || landlord.landlordLevelOverride}
                        </span>}
                    {metrics.integrationLandlord &&
                        <span className="badge badge-light text-dark ml-2">Integration</span>}
                </h4>

                {landlord.landlordLevelFailingSince &&
                    <div className="alert alert-warning py-2">
                        Failing since {moment(landlord.landlordLevelFailingSince).format("DD/MM/YYYY")} — level drops
                        on {moment(landlord.landlordLevelFailingSince).add(30, "days").format("DD/MM/YYYY")} if not recovered.
                    </div>}

                <div className="row">
                    <Metric label="Active Listings" value={metrics.activeListings || 0}/>
                    <Metric label="Confirmed Bookings" value={metrics.confirmedReservations || 0}
                            goal={`${minConfirmed.topLandlord} top / ${minConfirmed.risingStar} rising`}
                            failed={failing("confirmed_reservations")}/>
                    <Metric label="Cancellation Rate" value={`${metrics.cancellationRate || 0}%`}
                            goal={`< ${thresholds.maxCancellationRate}%`}
                            failed={failing("cancellation_rate")}/>
                    <Metric label="Answer Ratio" value={`${metrics.answerRatio || 0}%`}
                            goal={`>= ${thresholds.minAnswerRatio}%`}
                            failed={failing("answer_ratio")}/>
                    {/* as integracoes recebem a disponibilidade do feed, por isso o criterio nao se lhes aplica */}
                    {!metrics.integrationLandlord &&
                        <Metric label="Since Availability Confirmed" value={formatDays(metrics.daysSinceAvailabilityUpdate)}
                                goal={`<= ${thresholds.maxDaysWithoutAvailabilityUpdate} days`}
                                failed={failing("availability_update")}/>}
                    <Metric label="Since Confirmed Booking" value={formatDays(metrics.daysSinceConfirmedReservation)}
                            goal={`<= ${thresholds.maxDaysWithoutConfirmedReservation} days`}
                            failed={failing("confirmed_reservation_recency")}/>
                </div>

                <div className="row mt-2">
                    <FailedList title="Fails for Top Landlord" failed={failedTop}/>
                    <FailedList title="Fails for Rising Star" failed={failedRising}/>
                </div>

                {metrics.computedAt &&
                    <small className="text-muted d-block mt-3">
                        Last calculated {moment(metrics.computedAt).format("DD/MM/YYYY HH:mm")}
                        {metrics.windowDays ? ` — rolling window of ${metrics.windowDays} days` : " — lifetime totals"}
                    </small>}
            </div>
        </div>
    );
};

export default LandlordLevelPanel;
